import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import type { SortingState } from "@tanstack/react-table";
import { Passport } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { Label } from "@/components/base/label";
import { EmptyValue } from "@/components/common";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { PageHeader } from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { Illustration } from "@/components/shared-assets/illustrations";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useTableSettings } from "@/hooks/use-table-settings";
import type { GlobalRole } from "@/services/organization";
import { organizationQueryOptions } from "@/services/organization";

const TABLE_KEY = "admin.roles";
const DEFAULT_PAGE_SIZE = 10;

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/roles",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();

	// Track current page for manual pagination
	const [pageIndex, setPageIndex] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [sorting, setSorting] = useState<SortingState>([]);

	// Track if persisted settings have been applied
	const hasAppliedPersistedSettings = useRef(false);

	// Table settings persistence (page size only)
	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		savePageSize,
	} = useTableSettings({
		tableKey: TABLE_KEY,
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	// Apply persisted settings (page size) when config has loaded
	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
	}, [settingsLoaded, tableSettings.pageSize]);

	// Fetch all global roles (includes both Platform and Organization roles)
	const {
		data: globalRoles = [],
		isLoading,
		isFetching,
		isError,
		error,
		refetch,
	} = useQuery(organizationQueryOptions.globalRoles.list());

	// Apply client-side pagination
	const data = useMemo(() => {
		const startIndex = pageIndex * pageSize;
		const endIndex = startIndex + pageSize;
		const userSort = sorting[0]?.id === "users" ? sorting[0] : undefined;
		const ranked = userSort
			? [...globalRoles].sort((a, b) => {
					const difference =
						(a.activeUserCount ?? 0) - (b.activeUserCount ?? 0);
					return userSort.desc ? -difference : difference;
				})
			: globalRoles;
		const page = ranked.slice(startIndex, endIndex);
		if (userSort) return page;
		return [...page].sort((a, b) => a.name.localeCompare(b.name));
	}, [globalRoles, pageIndex, pageSize, sorting]);

	// Calculate total pages for pagination controls
	const pageCount = useMemo(() => {
		if (!globalRoles.length) return 0;
		return Math.ceil(globalRoles.length / pageSize);
	}, [globalRoles.length, pageSize]);

	const handlePaginationChange = ({
		pageSize: newPageSize,
		pageIndex: newPageIndex,
	}: {
		pageSize: number;
		pageIndex: number;
	}) => {
		if (newPageSize !== pageSize) {
			setPageSize(newPageSize);
			setPageIndex(0); // Reset to first page when size changes
			savePageSize(newPageSize);
		} else if (newPageIndex !== pageIndex) {
			setPageIndex(newPageIndex);
		}
	};

	const createBasicColumns: ColumnDef<GlobalRole>[] = [
		{
			id: "roleName",
			accessorKey: "name",
			header: t("OrganizationRole.Name", { useFieldInfo: true }),
			enableSorting: true,
			meta: {
				headerProps: {
					isRowHeader: true,
				},
			},
			cell: ({ row }) => {
				const role = row.original;
				return (
					<div className="flex items-center gap-3">
						<Avatar
							alt={role.name}
							placeholder={<Passport className="size-6 text-gray-400" />}
							size="md"
						/>
						<div className="whitespace-nowrap">
							<p className="text-sm font-medium text-primary">
								{role.name ?? <EmptyValue />}
							</p>
						</div>
					</div>
				);
			},
		},
		{
			id: "description",
			accessorKey: "description",
			header: t("OrganizationRole.Description", { useFieldInfo: true }),
			enableSorting: true,
			cell: ({ row }) => {
				return row.original.description ? (
					<span className="text-sm font-medium text-primary">
						{row.original.description}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "scope",
			accessorKey: "scope",
			header: t("admin.roles.table.columns.scope"),
			enableSorting: false,
			cell: ({ row }) => {
				const scope = row.original.scope;
				return (
					<Label size="md" type="default" showIcon={false}>
						{scope === "Platform"
							? t("admin.roles.table.columns.platform")
							: t("admin.roles.table.columns.organization")}
					</Label>
				);
			},
		},
		{
			id: "users",
			accessorKey: "activeUserCount",
			header: t("admin.roles.table.columns.users"),
			enableSorting: true,
			cell: ({ row }) => (
				<span className="text-sm font-medium text-primary">
					{row.original.activeUserCount ?? 0}
				</span>
			),
		},
		{
			id: "id",
			accessorKey: "id",
			header: t("OrganizationRole.Id", { useFieldInfo: true }),
			enableSorting: true,
			cell: ({ row }) => {
				return row.original.id ? (
					<CopyableId value={row.original.id} />
				) : (
					<EmptyValue />
				);
			},
		},
	];

	// Handle refresh with loading state
	const [isRefreshing, setIsRefreshing] = useState(false);
	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			// Refetch current page
			await refetch();
		} finally {
			setIsRefreshing(false);
		}
	};

	const roleColumns = createBasicColumns;

	const handleRowClick = (role: GlobalRole) => {
		navigate({
			to: "/admin/roles/$roleId/details",
			// Use role name as the identifier since global roles don't have IDs
			params: { roleId: encodeURIComponent(role.name) },
		});
	};

	// Handle error state
	if (isError) {
		return (
			<div className="space-y-0">
				<div className="flex flex-col items-center justify-center py-12">
					<div className="text-center">
						<h3 className="text-sm font-medium text-error">
							{t("admin.roles.table.errors.failedToLoadRoles")}
						</h3>
						<p className="text-sm text-tertiary mt-1">{error?.message}</p>
						<Button
							size="sm"
							color="primary"
							onClick={handleRefresh}
							className="mt-3"
						>
							{t("common.actions.tryAgain")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	const noRoles = () => {
		return (
			<div className="flex flex-col items-center justify-center">
				<div className="relative flex items-center justify-center w-[480px] h-[480px]">
					<div className="relative flex flex-col items-center justify-center">
						<Illustration type="cloud" size="sm">
							<Passport className="size-6" />
						</Illustration>
						<div className="mt-4 text-center">
							<h2 className="font-semibold text-primary text-md">
								{t("admin.roles.table.errors.noRoles")}
							</h2>
							<p className="text-sm text-tertiary">
								{t("admin.roles.table.errors.rolesWillAppearHere")}
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<>
			<PageLayout
				header={
					<PageHeader
						type="landing"
						title={t("admin.roles.label")}
						description={t("admin.roles.descriptionText")}
					/>
				}
			>
				<CustomTableWrapper
					tableKey={TABLE_KEY}
					data={data}
					columns={roleColumns}
					enableSorting={true}
					onSortingChange={(next) => {
						setSorting(next);
						setPageIndex(0);
					}}
					onRowClick={handleRowClick}
					// Enhanced loading states
					isLoading={isLoading}
					isFetching={isFetching}
					isRefreshing={isRefreshing}
					onRefresh={handleRefresh}
					// Manual pagination
					paginationMode="server"
					externalPaginationState={{
						pageSize,
						pageIndex,
					}}
					onPaginationChange={handlePaginationChange}
					pageCount={pageCount}
					rowCount={globalRoles.length}
					totalRowCount={globalRoles.length}
					// showRefreshButton={true}
					// Existing props
					showCard={true}
					showHeaderPadding={false}
					zebraStriping
				/>

				{!isLoading && !isFetching && (data?.length ?? 0) === 0
					? noRoles()
					: null}
			</PageLayout>

			<Outlet />
		</>
	);
}
